import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

const Landing = () => import('@/views/Landing.vue');
const Login = () => import('@/views/Login.vue');
const Register = () => import('@/views/Register.vue');
const ClubsList = () => import('@/views/ClubsList.vue');
const AdminClubs = () => import('@/views/AdminClubs.vue');
const Library = () => import('@/views/Library.vue');
const Exercises = () => import('@/views/Exercises.vue');
const ExerciseEdit = () => import('@/views/ExerciseEdit.vue');
const Inventory = () => import('@/views/Inventory.vue');
const InventoryEdit = () => import('@/views/InventoryEdit.vue');
const Cycles = () => import('@/views/Cycles.vue');
const CycleEdit = () => import('@/views/CycleEdit.vue');
const TrainingEdit = () => import('@/views/TrainingEdit.vue');
const TrainingView = () => import('@/views/TrainingView.vue');
const MyPlan = () => import('@/views/MyPlan.vue');
const MyZones = () => import('@/views/MyZones.vue');
const Groups = () => import('@/views/Groups.vue');
const GroupEdit = () => import('@/views/GroupEdit.vue');
const GroupSessionEdit = () => import('@/views/GroupSessionEdit.vue');
const GroupSessionView = () => import('@/views/GroupSessionView.vue');
const News = () => import('@/views/News.vue');
const NewsEdit = () => import('@/views/NewsEdit.vue');
const Students = () => import('@/views/Students.vue');
const Coaches = () => import('@/views/Coaches.vue');
const StudentZones = () => import('@/views/StudentZones.vue');
const Invites = () => import('@/views/Invites.vue');
const Profile = () => import('@/views/Profile.vue');
const ClubSettings = () => import('@/views/ClubSettings.vue');
const ClubSettingsGeneral = () => import('@/views/ClubSettingsGeneral.vue');
const ClubContacts = () => import('@/views/ClubContacts.vue');
const CoachCalendar = () => import('@/views/CoachCalendar.vue');
const ExerciseCategories = () => import('@/views/ExerciseCategories.vue');
const ExerciseCategoryEdit = () => import('@/views/ExerciseCategoryEdit.vue');
const NotFound = () => import('@/views/NotFound.vue');
const Today = () => import('@/views/Today.vue');
const RoleRedirect = () => import('@/views/RoleRedirect.vue');
const TrainingTemplates = () => import('@/views/TrainingTemplates.vue');
const TrainingTemplateEdit = () => import('@/views/TrainingTemplateEdit.vue');
const TrainingTemplateView = () => import('@/views/TrainingTemplateView.vue');

type RoleGuard = 'PlatformAdmin' | 'HeadCoach' | 'Coach' | 'Student';
type ClubRoleGuard = 'HeadCoach' | 'Coach' | 'Student';

declare module 'vue-router' {
  interface RouteMeta {
    requiresAuth?: boolean;
    roles?: RoleGuard[];
    clubRoles?: ClubRoleGuard[];
    public?: boolean;
  }
}

const routes: RouteRecordRaw[] = [
  { path: '/', component: Landing, meta: { public: true } },
  { path: '/login', component: Login, meta: { public: true } },
  { path: '/register/:code?', component: Register, meta: { public: true }, props: true },
  {
    path: '/admin',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { requiresAuth: true, roles: ['PlatformAdmin'] },
    children: [
      // Стартовая страница — лента новостей. AdminHome больше не доступен через /admin
      // напрямую, к нему можно вернуться отдельной ссылкой при необходимости.
      { path: '', redirect: '/admin/news' },
      { path: 'clubs', component: AdminClubs },
      { path: 'news', component: News },
      { path: 'news/new', component: NewsEdit, meta: { roles: ['PlatformAdmin'] } },
      { path: 'news/:id/edit', component: NewsEdit, meta: { roles: ['PlatformAdmin'] }, props: true },
      { path: 'profile', component: Profile },
    ],
  },
  {
    path: '/app',
    component: () => import('@/layouts/AppLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      // Корень /app: HC/Coach уходит на Today выбранного клуба,
      // Student — на «Мой план», иначе — лента новостей. Решение
      // принимает RoleRedirect (зависит от auth.user.memberships).
      { path: '', component: RoleRedirect },
      { path: 'clubs', component: ClubsList },
      { path: 'news', component: News },
      { path: 'news/new', component: NewsEdit },
      { path: 'news/:id/edit', component: NewsEdit, props: true },
      { path: 'profile', component: Profile },
      {
        path: 'clubs/:clubId',
        props: true,
        children: [
          // При входе в конкретный клуб — RoleRedirect для роли в этом
          // клубе: тренер → today, спортсмен → my-plan.
          { path: '', component: RoleRedirect },
          {
            path: 'today',
            component: Today,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach', 'Student'] },
          },
          {
            path: 'library',
            component: Library,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'training-templates',
            component: TrainingTemplates,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'training-templates/new',
            component: TrainingTemplateEdit,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'training-templates/:id/edit',
            component: TrainingTemplateEdit,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            // Read-only просмотр шаблона. Точка входа после создания
            // (POST → /:id) и при клике из списка шаблонов. Из view
            // карандашиком уходим в /edit.
            path: 'training-templates/:id',
            component: TrainingTemplateView,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'exercises',
            component: Exercises,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'exercises/new',
            component: ExerciseEdit,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'exercises/:id/edit',
            component: ExerciseEdit,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'inventory',
            component: Inventory,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'exercise-categories',
            component: ExerciseCategories,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'exercise-categories/new',
            component: ExerciseCategoryEdit,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'exercise-categories/:id/edit',
            component: ExerciseCategoryEdit,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'inventory/new',
            component: InventoryEdit,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'inventory/:id/edit',
            component: InventoryEdit,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'calendar',
            component: CoachCalendar,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'cycles',
            component: Cycles,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'cycles/new',
            component: CycleEdit,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'cycles/:id',
            component: CycleEdit,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'trainings/new',
            component: TrainingEdit,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'trainings/:id/edit',
            component: TrainingEdit,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            // Read-only просмотр тренировки — как видит её спортсмен в календаре.
            // Тренеры тоже сюда попадают (после создания/из карточки дня),
            // плюс редирект на edit-страницу через кнопку.
            path: 'trainings/:id',
            component: TrainingView,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach', 'Student'] },
          },
          {
            path: 'my-plan',
            component: MyPlan,
            props: true,
            meta: { clubRoles: ['Student'] },
          },
          {
            path: 'my-zones',
            component: MyZones,
            props: true,
            meta: { clubRoles: ['Student'] },
          },
          {
            path: 'groups',
            component: Groups,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'groups/new',
            component: GroupEdit,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'groups/:id',
            component: GroupEdit,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'groups/:id/sessions/new',
            component: GroupSessionEdit,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            // Редактор группового занятия. Только HC/Coach.
            path: 'groups/:id/sessions/:sessionId/edit',
            component: GroupSessionEdit,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            // Read-only просмотр группового занятия — как видит спортсмен и
            // как «лендится» тренер после создания / из календаря. Доступно
            // и студенту (если он в группе) — бэк сам это проверяет.
            path: 'groups/:id/sessions/:sessionId',
            component: GroupSessionView,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach', 'Student'] },
          },
          {
            path: 'students',
            component: Students,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'students/:studentId/zones',
            component: StudentZones,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            // Управление тренерами клуба (список / удаление / перенос). HC-only —
            // обычный Coach видит только свой контент, но не других коллег.
            path: 'coaches',
            component: Coaches,
            props: true,
            meta: { clubRoles: ['HeadCoach'] },
          },
          {
            path: 'invites',
            component: Invites,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            // Hub-страница «Настройки клуба»: тренерам видны только люди (группы,
            // спортсмены, приглашения), HC — ещё и параметры клуба.
            path: 'settings',
            component: ClubSettings,
            props: true,
            meta: { clubRoles: ['HeadCoach', 'Coach'] },
          },
          {
            path: 'settings/general',
            component: ClubSettingsGeneral,
            props: true,
            meta: { clubRoles: ['HeadCoach'] },
          },
          {
            path: 'contacts',
            component: ClubContacts,
            props: true,
            // Доступно всем участникам клуба; редактирование внутри ограничено HC.
            meta: { clubRoles: ['HeadCoach', 'Coach', 'Student'] },
          },
        ],
      },
    ],
  },
  { path: '/:pathMatch(.*)*', component: NotFound, meta: { public: true } },
];

export const router = createRouter({
  history: createWebHistory(),
  routes,
});

router.beforeEach((to) => {
  const auth = useAuthStore();
  if (to.meta.public) return true;
  if (to.meta.requiresAuth !== false && !auth.isAuthenticated) {
    return { path: '/login', query: { redirect: to.fullPath } };
  }

  const roleGuards = to.matched.flatMap((r) => r.meta.roles ?? []);
  if (roleGuards.length > 0) {
    const role = auth.role;
    if (!role || !roleGuards.includes(role as RoleGuard)) {
      return auth.isPlatformAdmin ? { path: '/admin/clubs' } : { path: '/app' };
    }
  }

  const clubRoleGuards = to.matched.flatMap((r) => r.meta.clubRoles ?? []);
  if (clubRoleGuards.length > 0) {
    const clubId = (to.params.clubId as string | undefined) ?? null;
    const clubRole = auth.roleInClub(clubId);
    if (!clubRole || !clubRoleGuards.includes(clubRole as ClubRoleGuard)) {
      return { path: '/app/clubs' };
    }
  }

  return true;
});
